import { cx } from "@emotion/css";
import Drawer from "@mui/material/Drawer";
import Fab from "@mui/material/Fab";
import Skeleton from "@mui/material/Skeleton";
import { Menu } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { SwipeableContent } from "src/components/Default/LekUI/SwipableDrawer";
import { GuideContents } from "src/components/Guide/GuideContents";
import { GuideNavigation } from "src/components/Guide/GuideNavigation";
import { GuideSection } from "src/components/Guide/GuideSection";
import { useSlot } from "src/components/Slots/SlotsContext";
import { useContainerQueryContext } from "src/containers/Layouts/AppContentContext";
import { LayoutWithSearch } from "src/containers/Layouts/LayoutWithSearch";
import isMobile from "src/helpers/isMobile";
import { useFaq } from "src/hooks/useFaq";

const CONTENTS_DRAWER_BREAKPOINT = 800;

export const Guide = () => {
	const { t } = useTranslation();
	const { width } = useContainerQueryContext();
	const isNarrow = width < CONTENTS_DRAWER_BREAKPOINT;
	const { sectionId } = useParams<{ sectionId?: string }>();
	const { data = [], isLoading } = useFaq();
	const [isContentsOpen, setIsContentsOpen] = useState(false);
	const AdminContents = useSlot("guideContents");
	const section = data.find(({ id }) => id === Number(sectionId)) ?? data[0];
	const hasNavigation = Boolean(section) && data.length > 1;

	const renderSection = () => {
		if (!section) {
			return (
				<div className="basic-box flex-1 size-16 text-grey">
					{t("guide.empty", "Poradnik jest w przygotowaniu.")}
				</div>
			);
		}

		return (
			<GuideSection
				key={section.id}
				section={section}
				footer={
					hasNavigation && (
						<GuideNavigation sections={data} selectedId={section.id} />
					)
				}
			/>
		);
	};

	const contents = (
		<GuideContents
			sections={data}
			selectedId={section?.id}
			onSelect={() => setIsContentsOpen(false)}
		/>
	);

	const renderContent = () => {
		if (isLoading) {
			return (
				<Skeleton
					variant="rounded"
					height={320}
					className="border-radius-32 flex-1"
				/>
			);
		}

		return (
			<>
				{renderSection()}
				{(section || AdminContents) && (
					<>
						{isNarrow && !isMobile() ? (
							<Drawer
								anchor="right"
								className="side-drawer"
								open={isContentsOpen}
								onClose={() => setIsContentsOpen(false)}
							>
								{contents}
							</Drawer>
						) : (
							<SwipeableContent
								isOpen={isContentsOpen}
								onClose={() => setIsContentsOpen(false)}
							>
								{contents}
							</SwipeableContent>
						)}
						{isNarrow && (
							<Fab
								className={cx("fab", { "fab--above-bar": hasNavigation })}
								onClick={() => setIsContentsOpen(true)}
								aria-label={t("articles.contents", "Spis treści")}
								data-test-id="guide-contents-fab"
							>
								<Menu className="icon-normal" />
							</Fab>
						)}
					</>
				)}
			</>
		);
	};

	return (
		<LayoutWithSearch className="min-h-100">
			<div
				className={cx("article--container w-100 items-start flex-1", {
					"pb-64": isNarrow && !hasNavigation,
					"pb-0": hasNavigation,
				})}
			>
				{renderContent()}
			</div>
		</LayoutWithSearch>
	);
};
